import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';

function GoodsReceipt({ userRole }) {
  const { expenseId } = useParams();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]); // [{expense_line_id, item_name, item_sku, uom, ordered_qty, received_to_date, remaining, received_now}]
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/api/expenses/receivable-lines', { params: { expenseId } });
        const data = (res.data || []).map(r => ({ ...r, received_now: '' }));
        setRows(data);
      } catch (e) {
        console.error('Failed to load receivable lines', e);
        alert('Failed to load receivable lines');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [expenseId]);

  const totalReceiving = useMemo(() => rows.reduce((s, r) => s + (Number(r.received_now) || 0) * (Number(r.unit_price) || 0), 0), [rows]);

  const updateRow = (id, patch) => setRows(prev => prev.map(r => (r.expense_line_id === id ? { ...r, ...patch } : r)));

  const validate = () => {
    const hasAny = rows.some(r => Number(r.received_now) > 0);
    if (!hasAny) return 'Enter a Received Now quantity for at least one line.';
    for (const r of rows) {
      const recv = Number(r.received_now || 0);
      if (recv < 0) return 'Received Now cannot be negative.';
      // Note: Over-receipt validation removed - now allows receiving more than ordered
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { alert(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        receipt_note: note || null,
        lines: rows
          .filter(r => Number(r.received_now) > 0)
          .map(r => ({ expense_line_id: r.expense_line_id, received_now: Number(r.received_now) })),
      };
      await api.post('/api/goods-receipts/goods-receipts', payload, { params: { expenseId } });
      alert('Goods receipt posted');
      // Refresh table
      const res = await api.get('/api/expenses/receivable-lines', { params: { expenseId } });
      const data = (res.data || []).map(r => ({ ...r, received_now: '' }));
      setRows(data);
      setNote('');
    } catch (e) {
      console.error('Post goods receipt failed', e);
      alert(e?.response?.data?.message || 'Failed to post goods receipt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" style={{ color: 'var(--text)' }}>
      <h1 className="text-xl font-bold">Goods Receipt</h1>
      <p className="text-sm" style={{ color: 'var(--muted)' }}>
        Record quantities received against approved inventory lines. Expense ID: <strong>{expenseId}</strong>
      </p>

      <div className="p-4 rounded border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
        {loading ? (
          <div className="text-sm" style={{ color: 'var(--muted)' }}>Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b" style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--muted)' }}>
                <tr>
                  <th className="p-2 text-left">Item</th>
                  <th className="p-2 text-left">UOM</th>
                  <th className="p-2 text-right">Ordered</th>
                  <th className="p-2 text-right">Received-to-date</th>
                  <th className="p-2 text-right">Remaining</th>
                  <th className="p-2 text-right">Received Now</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.expense_line_id} className="border-t" style={{ borderColor: 'var(--border)' }}>
                    <td className="p-2">{r.item_name || r.free_text_name} {r.item_sku ? `(${r.item_sku})` : ''}</td>
                    <td className="p-2">{r.uom}</td>
                    <td className="p-2 text-right">{Number(r.ordered_qty || 0)}</td>
                    <td className="p-2 text-right">{Number(r.received_to_date || 0)}</td>
                    <td className="p-2 text-right">{Number(r.remaining || 0)}</td>
                    <td className="p-2 text-right">
                      <input
                        type="number"
                        min="0"
                        max={Number(r.remaining || 0)}
                        value={r.received_now}
                        onChange={e => {
                          const val = e.target.value;
                          const n = val === '' ? '' : Number(val);
                          if (n === '' || (n >= 0 && n <= Number(r.remaining || 0))) updateRow(r.expense_line_id, { received_now: val });
                          else updateRow(r.expense_line_id, { received_now: String(Math.max(0, Math.min(Number(r.remaining || 0), n))) });
                        }}
                        className="w-28 border rounded px-2 py-1 text-right"
                        style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div>
                <label className="text-sm mb-1 block">Receipt Note (optional)</label>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                  placeholder="e.g., Delivered by vendor on 10/03"
                />
              </div>
              <div className="text-right text-sm font-semibold">Total Value Receiving Now: ₹ {totalReceiving.toFixed(2)}</div>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                disabled={submitting}
                onClick={handleSubmit}
                className="px-4 py-2 rounded font-semibold border"
                style={{ background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary-700)' }}
              >
                {submitting ? 'Posting…' : 'Post Goods Receipt'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GoodsReceipt;
